/**
 * Ad Account Manager Page
 * E007-S029: Settings page for managing connected ad accounts
 * 
 * Features:
 * - View connected accounts grouped by platform (Facebook, Google, TikTok)
 * - Add new accounts via OAuth
 * - Configure account settings (account type, pages, pixels, metrics)
 * - Reauthenticate expired tokens
 * - Disconnect accounts
 */

import React, { useState, useEffect, useCallback } from 'react';

// Simple toast replacement (use react-hot-toast if available)
const toast = {
  success: (msg: string) => console.log('✓', msg),
  error: (msg: string) => console.error('✗', msg),
  loading: (msg: string, opts?: { id: string }) => console.log('⏳', msg),
  dismiss: (id: string) => {},
};
import {
  listAdAccounts,
  updateAccount,
  disconnectAccount,
  simulateOAuthFlow,
  connectAccountsFlow,
  reauthenticateAccount,
  formatSpend,
  formatDate,
  getStatusMessage,
} from '../lib/adAccountsApi';
import type {
  AdAccount,
  AdAccountListResponse,
  Platform,
  AccountType,
  AvailableAccount,
} from '../types/ad-accounts';
import {
  PLATFORM_INFO,
  ACCOUNT_TYPE_LABELS,
  STATUS_INFO,
  AVAILABLE_METRICS,
} from '../types/ad-accounts';

// ============================================================================
// STATUS BADGE COMPONENT
// ============================================================================

const StatusBadge: React.FC<{ status: AdAccount['status'] }> = ({ status }) => {
  const info = STATUS_INFO[status];
  return (
    <div className="flex items-center gap-2">
      <div className={`w-2.5 h-2.5 rounded-full ${info.dotColor}`} />
      <span className={`text-sm ${info.color}`}>{info.label}</span>
    </div>
  );
};

// ============================================================================
// PLATFORM SECTION COMPONENT
// ============================================================================

interface PlatformSectionProps {
  platform: Platform;
  accounts: AdAccount[];
  totalSpend: number;
  onAddAccount: (platform: Platform) => void;
  onReauthenticate: (platform: Platform) => void;
  onUpdateAccount: (account: AdAccount, updates: { account_type?: AccountType; config?: any }) => void;
  onDisconnect: (account: AdAccount) => void;
  expandedAccount: string | null;
  setExpandedAccount: (id: string | null) => void;
}

const PlatformSection: React.FC<PlatformSectionProps> = ({
  platform,
  accounts,
  totalSpend,
  onAddAccount,
  onReauthenticate,
  onUpdateAccount,
  onDisconnect,
  expandedAccount,
  setExpandedAccount,
}) => {
  const info = PLATFORM_INFO[platform];
  const hasWarningAccounts = accounts.some(a => a.status === 'warning' || a.status === 'disconnected');

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{info.icon}</span>
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {info.name} Ad Accounts
          </h3>
          {accounts.length > 0 && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              ({accounts.length})
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasWarningAccounts && (
            <button
              onClick={() => onReauthenticate(platform)}
              className="px-3 py-1.5 text-sm border border-yellow-500 text-yellow-600 dark:text-yellow-400 rounded-md hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
            >
              Reauthenticate
            </button>
          )}
          <button
            onClick={() => onAddAccount(platform)}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-1"
          >
            <span>+</span> Add Account
          </button>
        </div>
      </div>

      {/* Content */}
      {accounts.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            You haven't connected {info.name} accounts yet
          </p>
          <button
            onClick={() => onAddAccount(platform)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            + Add {info.name} Account
          </button>
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-600 text-left text-sm text-gray-500 dark:text-gray-400">
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Account Name</th>
                  <th className="px-4 py-3 font-medium">Last 30 Days</th>
                  <th className="px-4 py-3 font-medium">Account Type</th>
                  <th className="px-4 py-3 font-medium">Timezone</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <React.Fragment key={account.id}>
                    <tr
                      className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer"
                      onClick={() => setExpandedAccount(
                        expandedAccount === account.id ? null : account.id
                      )}
                    >
                      <td className="px-4 py-3">
                        <StatusBadge status={account.status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400">
                            {expandedAccount === account.id ? '▼' : '▶'}
                          </span>
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {account.name}
                            </div>
                            {account.config?.special_ad_category && (
                              <div className="text-xs text-yellow-600 dark:text-yellow-400">
                                *{account.config.special_ad_category.replace(/_/g, ' ')}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-900 dark:text-white">
                        {formatSpend(account.last_30_day_spend, account.currency)}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={account.account_type}
                          onChange={(e) => {
                            e.stopPropagation();
                            onUpdateAccount(account, { account_type: e.target.value as AccountType });
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          {Object.entries(ACCOUNT_TYPE_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        {account.timezone || 'Not set'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {account.status !== 'connected' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onReauthenticate(platform);
                              }}
                              className="p-1.5 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded"
                              title="Reauthenticate"
                            >
                              🔄
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm(`Are you sure you want to disconnect "${account.name}"? Historical data will be retained.`)) {
                                onDisconnect(account);
                              }
                            }}
                            className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                            title="Disconnect"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                    {/* Expanded Config Row */}
                    {expandedAccount === account.id && (
                      <tr className="bg-gray-50 dark:bg-gray-700/30">
                        <td colSpan={6} className="px-6 py-4">
                          <AccountConfigPanel
                            account={account}
                            onUpdate={(updates) => onUpdateAccount(account, updates)}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer with total spend */}
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-600 text-right">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Total {info.name} Ad Spend:
            </span>
            <span className="ml-2 font-semibold text-gray-900 dark:text-white">
              {formatSpend(totalSpend)}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

// ============================================================================
// ACCOUNT CONFIG PANEL (Expanded Row)
// ============================================================================

interface AccountConfigPanelProps {
  account: AdAccount;
  onUpdate: (updates: { config?: any }) => void;
}

const AccountConfigPanel: React.FC<AccountConfigPanelProps> = ({ account, onUpdate }) => {
  const config = account.config || {};

  return (
    <div className="grid grid-cols-2 gap-6">
      {/* Left Column */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Default Audience Exclusion
          </label>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {config.default_exclusions?.length || 0} Audiences
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Name Stamp
          </label>
          <input
            type="text"
            value={config.name_stamp || ''}
            onChange={(e) => onUpdate({ config: { name_stamp: e.target.value } })}
            placeholder="e.g., GPAds"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
        </div>

        {account.platform === 'facebook' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Special Ad Category
            </label>
            <select
              value={config.special_ad_category || 'none'}
              onChange={(e) => onUpdate({ config: { special_ad_category: e.target.value === 'none' ? null : e.target.value } })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="none">None</option>
              <option value="housing">Housing</option>
              <option value="employment">Employment</option>
              <option value="credit">Credit</option>
              <option value="issues_elections_politics">Issues, Elections or Politics</option>
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Top 3 Metrics to Display
          </label>
          <div className="flex flex-wrap gap-2">
            {AVAILABLE_METRICS.map((metric) => (
              <label key={metric.value} className="inline-flex items-center">
                <input
                  type="checkbox"
                  checked={config.top_metrics?.includes(metric.value) ?? false}
                  onChange={(e) => {
                    const current = config.top_metrics || ['roas', 'cpm', 'ctr'];
                    let updated;
                    if (e.target.checked) {
                      if (current.length >= 3) {
                        toast.error('Maximum 3 metrics');
                        return;
                      }
                      updated = [...current, metric.value];
                    } else {
                      updated = current.filter((m: string) => m !== metric.value);
                    }
                    onUpdate({ config: { top_metrics: updated } });
                  }}
                  className="rounded border-gray-300 text-blue-600 mr-1.5"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{metric.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Right Column */}
      <div className="space-y-4">
        {account.platform === 'facebook' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Facebook Page
              </label>
              <select
                value={config.facebook_page_id || ''}
                onChange={(e) => onUpdate({ config: { facebook_page_id: e.target.value } })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="">Select a page...</option>
                <option value="page_goodpower">GoodPower</option>
                <option value="page_ace">ACE International</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Instagram Profile
              </label>
              <select
                value={config.instagram_profile_id || ''}
                onChange={(e) => onUpdate({ config: { instagram_profile_id: e.target.value } })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="">Select a profile...</option>
                <option value="ig_goodpower">@goodpower</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Pixel
              </label>
              <select
                value={config.pixel_id || ''}
                onChange={(e) => onUpdate({ config: { pixel_id: e.target.value } })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="">Select a pixel...</option>
                <option value="pixel_main">Main Website Pixel</option>
                <option value="pixel_app">App Install Pixel</option>
              </select>
            </div>
          </>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Industry
          </label>
          <select
            value={config.industry || 'other'}
            onChange={(e) => onUpdate({ config: { industry: e.target.value } })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          >
            <option value="ecommerce">E-commerce</option>
            <option value="saas">SaaS</option>
            <option value="finance">Finance</option>
            <option value="healthcare">Healthcare</option>
            <option value="education">Education</option>
            <option value="real_estate">Real Estate</option>
            <option value="other">Other</option>
          </select>
        </div>

        {/* Connection Info */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Connected: {formatDate(account.connected_at)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Last Sync: {formatDate(account.last_sync_at)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Status: {getStatusMessage(account)}
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ACCOUNT SELECTION MODAL
// ============================================================================

interface AccountSelectionModalProps {
  platform: Platform;
  availableAccounts: AvailableAccount[];
  onSelect: (accountIds: string[]) => void;
  onClose: () => void;
  isLoading: boolean;
}

const AccountSelectionModal: React.FC<AccountSelectionModalProps> = ({
  platform,
  availableAccounts,
  onSelect,
  onClose,
  isLoading,
}) => {
  const [selected, setSelected] = useState<string[]>([]);
  const info = PLATFORM_INFO[platform];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {info.icon} Select {info.name} Accounts
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Choose which accounts to connect
          </p>
        </div>

        <div className="px-6 py-4 max-h-64 overflow-y-auto">
          {availableAccounts.map((account) => (
            <label
              key={account.id}
              className="flex items-center gap-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 -mx-2 px-2 rounded"
            >
              <input
                type="checkbox"
                checked={selected.includes(account.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelected([...selected, account.id]);
                  } else {
                    setSelected(selected.filter(id => id !== account.id));
                  }
                }}
                className="rounded border-gray-300 text-blue-600"
              />
              <div>
                <div className="font-medium text-gray-900 dark:text-white">
                  {account.name}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {account.id} • {account.currency} • {account.timezone}
                </div>
              </div>
            </label>
          ))}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={() => onSelect(selected)}
            disabled={selected.length === 0 || isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Connecting...' : `Connect ${selected.length} Account(s)`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

const AdAccountManagerPage: React.FC = () => {
  const [data, setData] = useState<AdAccountListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedAccount, setExpandedAccount] = useState<string | null>(null);

  // OAuth flow state
  const [oauthPlatform, setOauthPlatform] = useState<Platform | null>(null);
  const [availableAccounts, setAvailableAccounts] = useState<AvailableAccount[]>([]);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  // Load accounts
  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const result = await listAdAccounts();
      setData(result);
    } catch (error) {
      console.error('Error loading accounts:', error);
      toast.error('Failed to load ad accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // Add account handler
  const handleAddAccount = async (platform: Platform) => {
    setOauthPlatform(platform);
    try {
      toast.loading(`Connecting to ${PLATFORM_INFO[platform].name}...`, { id: 'oauth' });
      
      // In local dev, simulate OAuth flow
      const result = await simulateOAuthFlow(platform);
      
      if (result.success && result.accounts.length > 0) {
        setAvailableAccounts(result.accounts.map(a => ({
          id: a.id,
          name: a.name,
          currency: 'USD',
          timezone: 'America/Los_Angeles'
        })));
        setShowAccountModal(true);
        toast.dismiss('oauth');
      } else {
        toast.dismiss('oauth');
        toast.error('No accounts found');
      }
    } catch (error) {
      toast.dismiss('oauth');
      toast.error('OAuth flow failed');
      console.error('OAuth error:', error);
    }
  };

  // Select accounts after OAuth
  const handleSelectAccounts = async (accountIds: string[]) => {
    if (!oauthPlatform || accountIds.length === 0) return;

    setIsConnecting(true);
    try {
      const result = await connectAccountsFlow(oauthPlatform, accountIds);
      toast.success(`Connected ${result.connected} account(s)`);
      setShowAccountModal(false);
      setOauthPlatform(null);
      setAvailableAccounts([]);
      loadAccounts();
    } catch (error) {
      toast.error('Failed to connect accounts');
      console.error('Connect error:', error);
    } finally {
      setIsConnecting(false);
    }
  };

  // Update account
  const handleUpdateAccount = async (
    account: AdAccount,
    updates: { account_type?: AccountType; config?: any }
  ) => {
    try {
      await updateAccount(account.id, updates);
      toast.success('Account updated');
      loadAccounts();
    } catch (error) {
      toast.error('Failed to update account');
      console.error('Update error:', error);
    }
  };

  // Disconnect account
  const handleDisconnect = async (account: AdAccount) => {
    try {
      await disconnectAccount(account.id);
      toast.success(`Disconnected "${account.name}"`);
      loadAccounts();
    } catch (error) {
      toast.error('Failed to disconnect account');
      console.error('Disconnect error:', error);
    }
  };

  // Reauthenticate handler
  const handleReauthenticate = (platform: Platform) => {
    toast.success(`Reauthentication for ${PLATFORM_INFO[platform].name} - would redirect to OAuth 🔐`);
    // In production, this would open OAuth popup for the platform
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">Loading ad accounts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 p-6">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Ad Accounts
        </h1>
        <p className="text-gray-500 dark:text-gray-400">
          Manage your connected advertising accounts across platforms
        </p>
      </div>

      {/* Settings Tabs (Simplified) */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-4">
          <button className="px-4 py-2 text-blue-600 border-b-2 border-blue-600 font-medium">
            Ad Accounts
          </button>
          <button className="px-4 py-2 text-gray-500 hover:text-gray-700 dark:text-gray-400">
            Products & Usage
          </button>
          <button className="px-4 py-2 text-gray-500 hover:text-gray-700 dark:text-gray-400">
            Payments
          </button>
          <button className="px-4 py-2 text-gray-500 hover:text-gray-700 dark:text-gray-400">
            User Profile
          </button>
          <button className="px-4 py-2 text-gray-500 hover:text-gray-700 dark:text-gray-400">
            Team Members
          </button>
        </nav>
      </div>

      {/* Platform Sections */}
      <div className="space-y-6">
        <PlatformSection
          platform="facebook"
          accounts={data?.facebook || []}
          totalSpend={data?.facebook_total_spend || 0}
          onAddAccount={handleAddAccount}
          onReauthenticate={handleReauthenticate}
          onUpdateAccount={handleUpdateAccount}
          onDisconnect={handleDisconnect}
          expandedAccount={expandedAccount}
          setExpandedAccount={setExpandedAccount}
        />

        <PlatformSection
          platform="google"
          accounts={data?.google || []}
          totalSpend={data?.google_total_spend || 0}
          onAddAccount={handleAddAccount}
          onReauthenticate={handleReauthenticate}
          onUpdateAccount={handleUpdateAccount}
          onDisconnect={handleDisconnect}
          expandedAccount={expandedAccount}
          setExpandedAccount={setExpandedAccount}
        />

        <PlatformSection
          platform="tiktok"
          accounts={data?.tiktok || []}
          totalSpend={data?.tiktok_total_spend || 0}
          onAddAccount={handleAddAccount}
          onReauthenticate={handleReauthenticate}
          onUpdateAccount={handleUpdateAccount}
          onDisconnect={handleDisconnect}
          expandedAccount={expandedAccount}
          setExpandedAccount={setExpandedAccount}
        />
      </div>

      {/* Account Selection Modal */}
      {showAccountModal && oauthPlatform && (
        <AccountSelectionModal
          platform={oauthPlatform}
          availableAccounts={availableAccounts}
          onSelect={handleSelectAccounts}
          onClose={() => {
            setShowAccountModal(false);
            setOauthPlatform(null);
            setAvailableAccounts([]);
          }}
          isLoading={isConnecting}
        />
      )}
    </div>
  );
};

export default AdAccountManagerPage;
